<script setup lang="ts">
import { computed, defineProps } from 'vue'
import PPanel from 'primevue/panel'
import PAccordion from 'primevue/accordion'
import PAccordionTab from 'primevue/accordiontab'
import CContentQuizAnswerChoice from '@/presentation/components/content/CContentQuizAnswerChoice.vue'
import CContentQuizAnswerSequencing from '@/presentation/components/content/CContentQuizAnswerSequencing.vue'
import CContentQuizAnswerMatching from '@/presentation/components/content/CContentQuizAnswerMatching.vue'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'
import {
  QuizQuestionAnswerMatching,
  type QuizQuestionAnswerSequencing
} from '@/core/domain/quiz/QuizQuestionAnswer.ts'

const props = defineProps<{
  question: QuizQuestion
}>()

const labelType = computed(() => {
  switch (props.question.type) {
    case LearningQuizQuestionType.Values.SINGLE_CHOICE:
      return 'Choix unique'
    case LearningQuizQuestionType.Values.MULTIPLE_CHOICE:
      return 'Choix multiple'
    case LearningQuizQuestionType.Values.TRUE_FALSE:
      return 'Vrai/Faux'
    case LearningQuizQuestionType.Values.MATCHING:
      return 'Associer'
    case LearningQuizQuestionType.Values.SEQUENCING:
      return 'Réordonner'
    default:
      return 'Inconnu'
  }
})

const isChoiceQuestion = computed(() => {
  return (
    props.question.type === LearningQuizQuestionType.Values.SINGLE_CHOICE ||
    props.question.type === LearningQuizQuestionType.Values.MULTIPLE_CHOICE ||
    props.question.type === LearningQuizQuestionType.Values.TRUE_FALSE
  )
})

const isMatchingQuestion = computed(() => {
  return props.question.type === LearningQuizQuestionType.Values.MATCHING
})

const isSequencingQuestion = computed(() => {
  return props.question.type === LearningQuizQuestionType.Values.SEQUENCING
})

const answers = computed(() => {
  return props.question.answers
})
</script>

<template>
  <p-panel class="bg-white p-2" :class="$style.QuestionPanel">
    <template #header>
      <h5 class="text-lg font-bold">Question {{ question.order }}</h5>
    </template>
    <template #icons>
      <p>{{ labelType }}</p>
    </template>

    <p-accordion :multiple="true" :active-index="[0]">
      <p-accordion-tab>
        <template #header>
          <h6 class="font-bold">{{ question.label }}</h6>
        </template>

        <div class="flex flex-col justify-start gap-1">
          <template v-if="isChoiceQuestion">
            <CContentQuizAnswerChoice
              v-for="(answer, idx) in answers"
              :key="`choice_${question.id}_${answer}_${idx}`"
              :answer="answer"
            />
          </template>
          <template v-else-if="isMatchingQuestion">
            <CContentQuizAnswerMatching
              v-for="(answer, idx) in answers"
              :key="`matching_${question.id}_${answer}_${idx}`"
              :answer="answer as QuizQuestionAnswerMatching"
              :order="idx + 1"
            />
          </template>
          <template v-else-if="isSequencingQuestion">
            <CContentQuizAnswerSequencing
              v-for="(answer, idx) in answers"
              :key="`sequencing_${question.id}_${answer}_${idx}`"
              :answer="answer as QuizQuestionAnswerSequencing"
            />
          </template>
          <template v-else>
            <p>Type de question non-reconnu</p>
          </template>
        </div>
      </p-accordion-tab>
    </p-accordion>
  </p-panel>
</template>

<style module>
.QuestionPanel {
  color: var(--bo-primary);

  :global(.p-panel-header) {
    border-bottom: 0;
    padding: 0.5rem 0.75rem;
    color: var(--bo-primary);
    background-color: var(--bo-white);
  }

  :global(.p-panel-content) {
    padding: 0.25rem 0.75rem;
  }

  :global(.p-accordion-header-link) {
    padding: 0;
    border-radius: 0;
    background-color: var(--bo-white);
    color: var(--bo-primary);
    border: 0;
  }

  :global(.p-accordion-content) {
    border: 0;
    padding: 0;
  }

  :global(.p-accordionpanel) {
    border: 0;
  }
}
</style>
